import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'src/app/auth/services/auth.service';
import { AppNotifications } from 'src/app/consts/app-notifications';
import { IBadResult } from 'src/app/interfaces/bad-result';
import { IProfile } from 'src/app/interfaces/profile';
import { ProfileModel } from 'src/app/models/profile-model';
import { NotificationService } from 'src/app/services/notification.service';
import { ProfileService } from 'src/app/services/profile.service';

import { ConfirmEmailComponent } from '../confirm-email/confirm-email.component';
import { PasswordComponent } from '../password/password.component';

export const thresholdOptions = Array.from({ length: 101 }, (_, i) => i).map(
  (n) => {
    return {
      id: n,
      name: n === 0 ? 'Disable low price warning' : `${n}%`,
    };
  }
);

@Component({
  selector: 'opt-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {
  public isReady = false;
  public isError = false;
  public profileForm: UntypedFormGroup;
  public badResult: IBadResult;
  public isPasswordHidden = true;
  public isLoading = false;

  private data: IProfile = null;
  private model: ProfileModel = new ProfileModel();

  constructor(
    private dialogRef: MatDialogRef<ProfileComponent>,
    private profileService: ProfileService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  get firstName() {
    return this.profileForm.get('firstName');
  }
  get lastName() {
    return this.profileForm.get('lastName');
  }
  get email() {
    return this.profileForm.get('email');
  }
  get password() {
    return this.profileForm.get('password');
  }
  get lowPriceThreshold() {
    return this.profileForm.get('lowPriceThreshold');
  }
  get thresholdOptions() {
    return thresholdOptions;
  }

  get cbIndividualSale() {
    return this.profileForm.get('cbIndividualSale');
  }
  get cbInventoryAdded() {
    return this.profileForm.get('cbInventoryAdded');
  }
  get cbPaymentComplete() {
    return this.profileForm.get('cbPaymentComplete');
  }
  get cbDailySummary() {
    return this.profileForm.get('cbDailySummary');
  }
  get cbWeeklySummary() {
    return this.profileForm.get('cbWeeklySummary');
  }

  get isEmailConfirmed() {
    return this.data.isEmailConfirmed;
  }
  get canAccessPayments() {
    return this.authService.canAccessPayments;
  }

  ngOnInit(): void {
    this.loadData();
  }

  public update(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }
    this.badResult = null;
    this.startLoading();
    this.model.populateFromFormGroup(this.profileForm);
    this.profileService.update(this.model).subscribe(
      (result) => {
        if (this.badResult) {
          this.badResult = null;
        }
        this.notificationService.show(result.message, { type: 'success' });
        this.dialogRef.close();
      },
      (error) => {
        this.badResult = error.error;
        this.stopLoading();
      },
      () => {
        this.stopLoading();
      }
    );
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public onChangePasswordClick(): void {
    this.dialog.open(PasswordComponent);
  }

  public onLogoutClick(): void {
    this.authService.logout().subscribe(() => {
      this.cancel();
    });
  }

  public onConfirmEmailClick(): void {
    const confirmDialog = this.dialog.open(ConfirmEmailComponent);
    confirmDialog.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.data.isEmailConfirmed = true;
        this.notificationService.show('Your email is confirmed.', {
          type: 'success',
        });
      }
    });
  }

  public selectInputContent(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.select();
  }

  private loadData(): void {
    this.profileService.get().subscribe(
      (result) => {
        this.data = result;
        this.initForm();
      },
      (error: HttpErrorResponse) => {
        this.setErrorState();
      }
    );
  }

  private initForm(): void {
    this.profileForm = new UntypedFormGroup({
      firstName: new UntypedFormControl(this.data.firstName, {
        validators: [
          Validators.required,
          this.validatePattern(/\S+/, { required: true }),
          Validators.maxLength(16),
        ],
        updateOn: 'blur',
      }),
      lastName: new UntypedFormControl(this.data.lastName, {
        validators: [
          Validators.required,
          this.validatePattern(/\S+/, { required: true }),
          Validators.maxLength(16),
        ],
        updateOn: 'blur',
      }),
      email: new UntypedFormControl(this.data.email, {
        validators: [Validators.required, Validators.email],
        updateOn: 'blur',
      }),
      password: new UntypedFormControl('', Validators.required),
      enableLowPriceThreshold: new FormControl<boolean>(
        this.data.enableLowPriceThreshold
      ),
      lowPriceThreshold: new FormControl<number>(this.data.lowPriceThreshold, {
        validators: [Validators.max(100), Validators.min(0)],
      }),
      cbIndividualSale: new UntypedFormControl(
        this.hasNotification(AppNotifications.emailInventorySale)
      ),
      cbInventoryAdded: new UntypedFormControl(
        this.hasNotification(AppNotifications.emailInventoryPurchase)
      ),
      cbPaymentComplete: new UntypedFormControl(
        this.hasNotification(AppNotifications.emailPaymentComplete)
      ),
      cbDailySummary: new UntypedFormControl(
        this.hasNotification(AppNotifications.emailDailySummary)
      ),
      cbWeeklySummary: new UntypedFormControl(
        this.hasNotification(AppNotifications.emailWeeklySportsReport)
      ),
    });

    this.isReady = true;
  }

  private startLoading() {
    this.isLoading = true;
    this.profileForm.disable();
  }

  private stopLoading() {
    this.isLoading = false;
    this.profileForm.enable();
  }

  private setErrorState() {
    this.isLoading = false;
    this.isError = true;
  }

  private validatePattern(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }

  private hasNotification(notification: string): boolean {
    return this.data.notifications.indexOf(notification) !== -1;
  }
}
